import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Badge
} from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import AllergenModal from "./AllergenModal";

const isEmptyHtmlBlock = (node) => {
  if (!node) return true;
  if (node.nodeType === Node.TEXT_NODE) {
    return !(node.textContent || "").replace(/\u200b/g, "").trim();
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return true;

  const text = (node.textContent || "").replace(/\u200b/g, "").trim();
  return text === "";
};

const buildAllergenSectionHtml = (allergenNames = []) => {
  if (!allergenNames.length) return "";

  // Keep allergens in one paragraph with <br> so display stays compact
  const lines = [
    "⚠️ <strong>Allergens</strong>",
    ...allergenNames.map((name) => `• ${name}`)
  ];

  return `<p>${lines.join("<br>")}</p>`;
};

const syncAllergensInScript = (html = "", allergenNames = []) => {
  if (typeof document === "undefined") {
    const section = buildAllergenSectionHtml(allergenNames);
    return section || html || "";
  }

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html || "";

  const children = Array.from(wrapper.childNodes);
  let allergenStartIdx = -1;

  for (let i = 0; i < children.length; i++) {
    const text = children[i].textContent || "";
    if (text.includes("⚠️") && /Allergens/i.test(text)) {
      allergenStartIdx = i;
      break;
    }
  }

  if (allergenStartIdx >= 0) {
    let removeFrom = allergenStartIdx;
    // Drop blank blocks immediately before Allergens (extra section gap)
    while (removeFrom > 0 && isEmptyHtmlBlock(children[removeFrom - 1])) {
      removeFrom -= 1;
    }
    for (let i = children.length - 1; i >= removeFrom; i -= 1) {
      wrapper.removeChild(children[i]);
    }
  }

  while (wrapper.lastChild && isEmptyHtmlBlock(wrapper.lastChild)) {
    wrapper.removeChild(wrapper.lastChild);
  }

  const section = buildAllergenSectionHtml(allergenNames);
  if (!section) return wrapper.innerHTML;
  if (!wrapper.innerHTML) return section;

  wrapper.insertAdjacentHTML("beforeend", section);
  return wrapper.innerHTML;
};

const EditFlashcardModal = ({ isOpen, toggle, card, onSuccess }) => {
  const [frontText, setFrontText] = useState("");
  const [script, setScript] = useState(card.format_coching_script || "");
  const [saving, setSaving] = useState(false);
  // Only set when a new image is uploaded (raw S3 filename, not full URL)
  const [imageName, setImageName] = useState("");
  const [previewImage, setPreviewImage] = useState(card?.image_name || "");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [allergens, setAllergens] = useState([]);
  const [loadingAllergens, setLoadingAllergens] = useState(false);
  const [showAllergenModal, setShowAllergenModal] = useState(false);

  const normalizeScriptToHTML = (raw = "") => {
    return raw
      ?.replace(/\\r\\n|\\n|\\r/g, "")
      ?.replace(/<\/div>\s*<div[^>]*>/gi, "<br>")
      ?.replace(/<\/?div[^>]*>/gi, "")
      ?.replace(/(<br\s*\/?>)(\s|&nbsp;|\r|\n)+/gi, "<br>")
      ?.replace(/\\'/g, "'")
      ?.replace(/\\"/g, '"');
  };

  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    onUpdate({ editor }) {
      setScript(editor.getHTML());
    }
  });

  const loadFlashCardAllergens = (flashId) => {
    if (!flashId) {
      setAllergens([]);
      return;
    }

    setLoadingAllergens(true);
    postDataAPI({
      serviceName: "item_data",
      op: "get_flash_card_allergens",
      flash_id: flashId
    })
      .then((res) => {
        if (res.status === "ok") {
          setAllergens(res.data || []);
        } else {
          setAllergens([]);
        }
      })
      .catch(() => setAllergens([]))
      .finally(() => setLoadingAllergens(false));
  };

  useEffect(() => {
    if (isOpen && card && editor) {
      setFrontText(`${card.item_name} - ${card.course_name}`);

      const html = normalizeScriptToHTML(card.format_coching_script || "");
      editor.commands.setContent(html);
      setScript(html);

      setImageName("");
      setPreviewImage(card.image_name || "");
      setRemoveImage(false);
      setUploadingImage(false);
      setShowAllergenModal(false);
      loadFlashCardAllergens(card.flash_id);
    }
  }, [isOpen, card, editor]);

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      setUploadingImage(true);
      const res = await postDataAPI({
        serviceName: "item_data",
        op: "uploadMenuItemImage",
        image: file
      });
      if (res.status === "ok") {
        setImageName(res.data.image_name);
        setRemoveImage(false);
        // Preview immediately
        setPreviewImage(URL.createObjectURL(file));
      }
      if (res.status === "error") {
        alert(res.message);
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = () => {
    setPreviewImage("");
    setImageName("");
    setRemoveImage(true);
  };

  const handleAllergensSave = (nextAllergens = []) => {
    setAllergens(nextAllergens);

    const allergenNames = nextAllergens
      .map((a) => a.allergen_name)
      .filter(Boolean);
    const currentHtml = editor?.getHTML?.() || script || "";
    let syncedHtml = syncAllergensInScript(currentHtml, allergenNames);

    if (editor) {
      editor.commands.setContent(syncedHtml);
      // TipTap may normalize HTML and reintroduce empty blocks; clean once more
      syncedHtml = syncAllergensInScript(editor.getHTML(), allergenNames);
      if (syncedHtml !== editor.getHTML()) {
        editor.commands.setContent(syncedHtml);
      }
      setScript(editor.getHTML());
    } else {
      setScript(syncedHtml);
    }
  };

  const handleSave = () => {
    if (!script.trim()) {
      alert("Coaching script is required");
      return;
    }

    setSaving(true);

    const payload = {
      serviceName: "item_data",
      op: "create_update_flash_card",
      item_id: card.item_id,
      coching_script: script,
      flash_card_id: card.flash_id,
      dishName: frontText,
      allergens: allergens
    };

    if (removeImage) {
      payload.remove_image = 1;
      payload.image_name = "";
    } else if (imageName) {
      // Only send newly uploaded filename (not the existing full URL)
      payload.image_name = imageName;
    }

    postDataAPI(payload)
      .then((res) => {
        if (res.status === "ok") {
          onSuccess?.();
          toggle();
        } else {
          alert(res.message || "Failed to update flashcard");
        }
      })
      .finally(() => setSaving(false));
  };

  if (!card) return null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle}>Edit Flashcard</ModalHeader>

        <ModalBody>
          <Label>Front (Dish Name & Category)</Label>
          <Input value={frontText} disabled className="mb-3" />

          <Label>Back (Coaching Script)</Label>
          <EditorContent
            editor={editor}
            className="border rounded p-2 mb-3"
            style={{ minHeight: 150 }}
          />

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Label className="mb-0">⚠️ Allergens</Label>
              <Button
                size="sm"
                outline
                color="warning"
                onClick={() => setShowAllergenModal(true)}
                disabled={loadingAllergens}
              >
                ⚠️ {allergens.length ? "Edit Allergens" : "Add Allergens"}
              </Button>
            </div>

            <div className="p-3 bg-light rounded">
              <small className="text-muted">Selected Allergens:</small>
              <div className="mt-2 d-flex gap-2 flex-wrap">
                {loadingAllergens ? (
                  <small className="text-muted">Loading allergens...</small>
                ) : allergens.length === 0 ? (
                  <small className="text-muted">
                    No allergens selected yet. Add allergens to include in the Allergens section of the flashcard.
                  </small>
                ) : (
                  allergens.map((allergen) => (
                    <Badge key={allergen.allergen_id} color="warning" pill>
                      {allergen.allergen_name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>

          <Label>Flashcard Image</Label>
          <div className="mb-3">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => uploadImage(e.target.files[0])}
            />
            {uploadingImage && (
              <small className="text-primary">Uploading...</small>
            )}
            {previewImage && (
              <div className="mt-2 d-flex align-items-start gap-2">
                <img
                  src={previewImage}
                  alt=""
                  style={{
                    width: 70,
                    borderRadius: 8
                  }}
                />
                <Button
                  color="danger"
                  outline
                  size="sm"
                  type="button"
                  onClick={handleDeleteImage}
                  disabled={uploadingImage || saving}
                  title="Delete image"
                >
                  <i className="ri-delete-bin-line"></i> Delete
                </Button>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button outline onClick={toggle}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || uploadingImage}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>

      <AllergenModal
        isOpen={showAllergenModal}
        toggle={() => setShowAllergenModal(false)}
        onSave={handleAllergensSave}
        selectedAllergens={allergens}
      />
    </>
  );
};

export default EditFlashcardModal;
